import React, { Component } from "react";
import { observer } from "mobx-react";
import PropTypes from "prop-types";
import { Form, Image, Button } from "semantic-ui-react";
import Address, {
  modelPropTypes as addressModelPropTypes,
  schemaPropTypes as addressSchemaPropTypes,
  addressValidation,
} from "~/components/element/Address/Address";
import Country from "~/models/Country";
import ImageUpload from "~/components/element/ImageUpload/ImageUpload";
import PromiseButton from "~/components/element/PromiseButton";
import ErrorWrapper from "~/components/wrapper/ErrorWrapper";
import ValidateSchema, { validateEmail } from "~/utils/ReactReformed/ValidateSchema";
import CreateCustomerStore from "../stores/CreateCustomerStore";
import "../styles/CreateCustomer.less";

@observer
class CustomerForm extends Component {
  allValid() {
    const { schema } = this.props;

    return schema.isValid;
  }

  submit() {
    const { model, createCustomerStore, customerCreatedCallback, showError } = this.props;

    if (!this.allValid()) {
      return Promise.resolve();
    }

    return createCustomerStore
      .submit(model)
      .then(() => {
        customerCreatedCallback && customerCreatedCallback();
      })
      .catch(error => showError(error.message));
  }

  render() {
    const {
      countries,
      model,
      isDirty,
      setModel,
      setProperty,
      schema,
      bindInput,
      getAggregatedError,
      createCustomerStore,
      goBackCallback,
    } = this.props;
    const { isLoading, isSaving, errorMessages } = createCustomerStore;

    return (
      // Show a loading icon if form is submitting or if countries are not loaded yet
      <Form className="create-customer" loading={isLoading || isSaving || countries.length === 0}>
        <Form.Group widths="equal">
          <Form.Input
            id="first-name"
            label="* First Name"
            {...bindInput("firstName", schema, errorMessages)}
            fluid
            placeholder="First Name"
          />
          <Form.Input
            id="last-name"
            label="* Last Name"
            {...bindInput("lastName", schema, errorMessages)}
            fluid
            placeholder="Last Name"
          />
        </Form.Group>
        <Form.Input
          id="contact-phone"
          label="* Contact Phone"
          {...bindInput("contactPhone", schema, errorMessages)}
          fluid
          placeholder="Contact Phone"
        />
        <Form.Input
          id="contact-email"
          label="* Contact Email"
          {...bindInput("email", schema, errorMessages)}
          fluid
          placeholder="Contact Email"
        />
        <Address
          countries={countries}
          model={model}
          isDirty={isDirty}
          setModel={setModel}
          schema={schema}
          serverValidationErrors={errorMessages}
          bindInput={bindInput}
          hasSelectedResult={!!createCustomerStore.selectedAddressResult}
          addressResults={createCustomerStore.addressResults}
          setAddressResults={results => createCustomerStore.setAddressResults(results)}
          setSelectedAddressResult={result => createCustomerStore.setSelectedAddressResult(result)}
        />
        <br />
        <Form.Input
          id="cell-phone"
          label="Cell Phone"
          {...bindInput("cellPhone", schema, errorMessages)}
          fluid
          placeholder="Cell Phone (only used for scheduling)"
        />
        <Form.Input
          id="company"
          label="Company"
          {...bindInput("company", schema, errorMessages)}
          fluid
          placeholder="Company"
        />
        <Form.Input
          id="website"
          label="Website"
          {...bindInput("website", schema, errorMessages)}
          fluid
          placeholder="Website"
        />
        <Form.Select
          fluid
          id="payment-collection-method"
          label="Payment Collection Method"
          loading={createCustomerStore.customerPaymentMethods.length === 0}
          placeholder="Payment Collection Method"
          value={model.defaultPaymentCollectionMethod || ""}
          name="defaultPaymentCollectionMethod"
          options={createCustomerStore.customerPaymentMethods}
          onChange={(_event, data) => setProperty("defaultPaymentCollectionMethod", data.value)}
          error={getAggregatedError("defaultPaymentCollectionMethod", schema, errorMessages)}
        />
        <Form.TextArea
          {...bindInput("assistantCcEmails", schema, errorMessages)}
          id="assistant-cc-emails"
          label="Assistant CC Emails"
          placeholder="Assistant cc email list - each on a separate line"
        />
        <Form.TextArea
          id="internal-notes"
          {...bindInput("internalNotes", schema, errorMessages)}
          label="Internal Notes"
          placeholder="Internal notes"
        />
        <br />
        <Image.Group size="small">
          <ImageUpload
            onFileSelected={encoded => createCustomerStore.setPhotoData(encoded)}
            defaultSrc="/tourbuilder/assets/images/user_sil.jpg"
          />
          &nbsp;&nbsp;
          <ImageUpload
            onFileSelected={encoded => createCustomerStore.setLogoData(encoded)}
            defaultSrc="/tourbuilder/assets/images/logo_goes_here.jpg"
          />
        </Image.Group>
        <div className="create-customer-actions">
          <Button secondary onClick={() => goBackCallback()}>
            Cancel
          </Button>
          <PromiseButton disabled={!this.allValid()} primary onClick={() => this.submit()}>
            Create Customer
          </PromiseButton>
        </div>
      </Form>
    );
  }
}

CustomerForm.propTypes = {
  // specific to react-reformed
  model: PropTypes.shape({
    ...addressModelPropTypes,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    company: PropTypes.string,
    email: PropTypes.string,
    contactPhone: PropTypes.string,
    cellPhone: PropTypes.string,
    website: PropTypes.string,
    defaultPaymentCollectionMethod: PropTypes.string,
    assistantCcEmails: PropTypes.string,
    internalNotes: PropTypes.string,
  }).isRequired,
  schema: PropTypes.shape({
    isValid: PropTypes.bool,
    fields: PropTypes.shape({
      ...addressSchemaPropTypes,
      firstName: PropTypes.object,
      lastName: PropTypes.object,
      email: PropTypes.object,
      contactPhone: PropTypes.object,
      assistantCcEmails: PropTypes.object,
    }).isRequired,
  }).isRequired,
  isDirty: PropTypes.bool,
  setModel: PropTypes.func.isRequired,
  setProperty: PropTypes.func.isRequired,
  bindInput: PropTypes.func.isRequired,
  getAggregatedError: PropTypes.func.isRequired,
  // everything else.
  countries: PropTypes.arrayOf(PropTypes.instanceOf(Country)),
  createCustomerStore: PropTypes.instanceOf(CreateCustomerStore),
  goBackCallback: PropTypes.func.isRequired,
  customerCreatedCallback: PropTypes.func,
};

const WrappedCustomerForm = ValidateSchema({
  ...addressValidation,
  firstName: {
    required: true,
  },
  lastName: {
    required: true,
  },
  email: {
    required: true,
    email: true,
  },
  contactPhone: {
    required: true,
  },
  assistantCcEmails: {
    test: (value, fail) => {
      if (typeof value === "undefined" || value === null) {
        return null;
      }

      const parts = value.split(/[\n]+/).filter(entry => entry.trim().length > 0);

      for (let i = 0; i < parts.length; i += 1) {
        const email = parts[i];

        if (validateEmail(email)) {
          return fail("invalid email");
        }
      }

      return null;
    },
  },
})(CustomerForm);

export default ErrorWrapper(WrappedCustomerForm);
